/**
 * @file pages/Moments.js
 * @description Page des 12 moments d'étude biblique
 */

import React from 'react';
import { Link } from 'react-router-dom';

const MOMENTS = [
  {
    id: 'romains',
    name: 'La connaissance du livre de Romains',
    description: 'Explorez la théologie profonde de Paul sur la grâce, la foi et la justice divine.',
    book: 'Romains',
    icon: '✦',
    active: true,
    gradient: 'linear-gradient(135deg, rgba(169, 134, 103, 0.25), rgba(169, 134, 103, 0.05))',
  },
  { id: 'genese', name: 'Les origines selon la Genèse', description: 'La création, la chute et les promesses fondatrices de toute l\'Écriture.', book: 'Genèse', icon: '🌿', active: false, gradient: 'linear-gradient(135deg, rgba(39, 174, 96, 0.15), transparent)' },
  { id: 'psaumes', name: 'La prière des Psaumes', description: 'Un voyage poétique à travers la louange, la lamentation et la confiance.', book: 'Psaumes', icon: '🎵', active: false, gradient: 'linear-gradient(135deg, rgba(52, 152, 219, 0.15), transparent)' },
  { id: 'jean', name: 'L\'Évangile selon Jean', description: 'L\'amour, la lumière et la vie éternelle révélés à travers le Verbe incarné.', book: 'Jean', icon: '💡', active: false, gradient: 'linear-gradient(135deg, rgba(241, 196, 15, 0.15), transparent)' },
  { id: 'proverbes', name: 'La sagesse des Proverbes', description: 'Principes divins pour une vie sage, juste et épanouissante.', book: 'Proverbes', icon: '⚖️', active: false, gradient: 'linear-gradient(135deg, rgba(155, 89, 182, 0.15), transparent)' },
  { id: 'corinthiens', name: 'L\'amour selon Corinthiens', description: 'La définition et la nature de l\'amour divin dans la communauté chrétienne.', book: '1 Corinthiens', icon: '❤️', active: false, gradient: 'linear-gradient(135deg, rgba(231, 76, 60, 0.15), transparent)' },
  { id: 'ephesiens', name: 'Les armures spirituelles', description: 'Comprendre l\'armor de Dieu et la vie dans le Corps de Christ.', book: 'Éphésiens', icon: '🛡️', active: false, gradient: 'linear-gradient(135deg, rgba(52, 73, 94, 0.3), transparent)' },
  { id: 'apocalypse', name: 'La révélation finale', description: 'Le dénouement de l\'histoire humaine et le triomphe du Royaume de Dieu.', book: 'Apocalypse', icon: '⚡', active: false, gradient: 'linear-gradient(135deg, rgba(230, 126, 34, 0.15), transparent)' },
  { id: 'matthieu', name: 'Le Sermon sur la montagne', description: 'Les Béatitudes et les enseignements fondamentaux de Jésus-Christ.', book: 'Matthieu', icon: '⛰️', active: false, gradient: 'linear-gradient(135deg, rgba(26, 188, 156, 0.15), transparent)' },
  { id: 'esaie', name: 'Les prophéties d\'Esaïe', description: 'La vision du Serviteur souffrant et de la gloire messianique à venir.', book: 'Esaïe', icon: '🌅', active: false, gradient: 'linear-gradient(135deg, rgba(189, 195, 199, 0.15), transparent)' },
  { id: 'actes', name: 'Les Actes des Apôtres', description: 'L\'Église primitive, le Saint-Esprit et l\'expansion du Royaume.', book: 'Actes', icon: '🔥', active: false, gradient: 'linear-gradient(135deg, rgba(231, 76, 60, 0.1), transparent)' },
  { id: 'daniel', name: 'La foi de Daniel', description: 'Intégrité, visions prophétiques et fidélité dans l\'adversité.', book: 'Daniel', icon: '🦁', active: false, gradient: 'linear-gradient(135deg, rgba(243, 156, 18, 0.15), transparent)' },
];

const Moments = () => {
  return (
    <>
      <style>{`
        .moments-page {
          min-height: 100vh;
          padding: 100px 24px 80px;
          background: linear-gradient(180deg, #1F2A44 0%, #141d30 100%);
        }
        .moments-header {
          max-width: 700px;
          margin: 0 auto 64px;
          text-align: center;
          animation: fadeInUp 0.5s ease;
        }
        .moments-label {
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 3px;
          text-transform: uppercase;
          color: #A98667;
          margin-bottom: 16px;
        }
        .moments-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.2rem, 5vw, 3.5rem);
          color: #FFFFFF;
          margin-bottom: 16px;
          line-height: 1.15;
        }
        .moments-desc {
          color: rgba(240, 234, 226, 0.6);
          font-size: 1rem;
          line-height: 1.7;
        }
        .moments-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
          gap: 24px;
        }
        .moment-card {
          border-radius: 16px;
          padding: 32px 28px;
          border: 1px solid rgba(169, 134, 103, 0.15);
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          animation: fadeInUp 0.5s ease both;
          text-decoration: none;
          display: block;
        }
        .moment-card.active {
          background: rgba(31, 42, 68, 0.6);
          cursor: pointer;
        }
        .moment-card.inactive {
          background: rgba(20, 29, 48, 0.4);
          cursor: not-allowed;
          opacity: 0.55;
        }
        .moment-card.active:hover {
          transform: translateY(-6px);
          border-color: rgba(169, 134, 103, 0.4);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(169, 134, 103, 0.15);
        }
        .moment-card.active:hover .moment-card-glow {
          opacity: 1;
        }
        .moment-card-glow {
          position: absolute;
          top: -50%;
          left: -50%;
          right: -50%;
          bottom: -50%;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.5s ease;
          background: radial-gradient(ellipse 80% 80% at 50% 50%, rgba(169, 134, 103, 0.06), transparent 70%);
        }
        .moment-card-bg {
          position: absolute;
          inset: 0;
          border-radius: 16px;
        }
        .moment-content {
          position: relative;
          z-index: 1;
        }
        .moment-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 16px;
        }
        .moment-icon {
          width: 52px;
          height: 52px;
          background: rgba(169, 134, 103, 0.12);
          border: 1px solid rgba(169, 134, 103, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
        }
        .moment-badge {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 50px;
        }
        .badge-active {
          background: rgba(169, 134, 103, 0.2);
          color: #A98667;
          border: 1px solid rgba(169, 134, 103, 0.3);
        }
        .badge-soon {
          background: rgba(255,255,255,0.05);
          color: rgba(240, 234, 226, 0.4);
          border: 1px solid rgba(255,255,255,0.08);
        }
        .moment-book {
          font-size: 0.8rem;
          font-weight: 600;
          color: #A98667;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .moment-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.25rem;
          color: #FFFFFF;
          line-height: 1.3;
          margin-bottom: 10px;
        }
        .moment-desc {
          font-size: 0.875rem;
          color: rgba(240, 234, 226, 0.55);
          line-height: 1.6;
        }
        .moment-arrow {
          position: absolute;
          bottom: 28px;
          right: 28px;
          width: 32px;
          height: 32px;
          background: rgba(169, 134, 103, 0.15);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #A98667;
          font-size: 0.85rem;
          transition: all 0.3s ease;
        }
        .moment-card.active:hover .moment-arrow {
          background: #A98667;
          color: #FFFFFF;
          transform: translateX(3px);
        }
        @media (max-width: 700px) {
          .moments-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="moments-page">
        <div className="moments-header">
          <p className="moments-label">✦ Parcours guidés</p>
          <h1 className="moments-title">Moments d'Étude<br />Biblique</h1>
          <p className="moments-desc">
            Chaque moment est une immersion dans un livre ou une thématique des Écritures. 
            Notez vos réflexions et avancez à votre propre rythme.
          </p>
        </div>

        <div className="moments-grid">
          {MOMENTS.map((moment, i) => (
            moment.active ? (
              <Link
                key={moment.id}
                to={`/moments/${moment.id}`}
                className="moment-card active"
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className="moment-card-bg" style={{ background: moment.gradient }} />
                <div className="moment-card-glow" />
                <div className="moment-content">
                  <div className="moment-header">
                    <div className="moment-icon">{moment.icon}</div>
                    <span className={`moment-badge badge-active`}>Disponible</span>
                  </div>
                  <div className="moment-book">{moment.book}</div>
                  <h2 className="moment-name">{moment.name}</h2>
                  <p className="moment-desc">{moment.description}</p>
                </div>
                <div className="moment-arrow">→</div>
              </Link>
            ) : (
              <div
                key={moment.id}
                className="moment-card inactive"
                style={{ animationDelay: `${i * 0.06}s` }}
                title="Ce moment sera bientôt disponible"
              >
                <div className="moment-card-bg" style={{ background: moment.gradient }} />
                <div className="moment-content">
                  <div className="moment-header">
                    <div className="moment-icon" style={{ opacity: 0.5 }}>{moment.icon}</div>
                    <span className="moment-badge badge-soon">Bientôt</span>
                  </div>
                  <div className="moment-book" style={{ opacity: 0.5 }}>{moment.book}</div>
                  <h2 className="moment-name">{moment.name}</h2>
                  <p className="moment-desc">{moment.description}</p>
                </div>
              </div>
            )
          ))}
        </div>
      </div>
    </>
  );
};

export default Moments;
